import { createContext, Suspense, useContext } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { QueryErrorBoundary } from '@gonfalon/error-boundaries/QueryErrorBoundary';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { DEFAULT_EXPERIMENT_EXPAND_OPTIONS } from '@gonfalon/experiments/defaultExperimentExpandOptions';
import type { Experiment, Iteration } from '@gonfalon/experiments/types';
import { useSuspenseExperiment } from '@gonfalon/experiments/useSuspenseExperiment';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { Message } from '@gonfalon/messages/Message';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useParam } from '@gonfalon/router/useParam';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useSuspenseQuery } from '@tanstack/react-query';

import { AppSkeletonBody } from '#components/SkeletonLoader/AppSkeletonBody';

type ExperimentIterationContext = {
  experiment: Experiment;
  iteration: Iteration;
  // update to "flags" once we support multiple
  flag?: FeatureFlag;
  isAiExperiment?: boolean;
};

const ExperimentIterationContext = createContext<ExperimentIterationContext | undefined>(undefined);

// exported for tests, typically you'll want to use WithExperimentIterationContextProvider instead
export function ExperimentIterationContextProvider({
  value,
  children,
}: {
  value: Pick<ExperimentIterationContext, 'experiment' | 'iteration' | 'flag'>;
  children: React.ReactNode;
}) {
  return (
    <ExperimentIterationContext.Provider
      value={{
        ...value,
        isAiExperiment: value.flag?._purpose === 'ai',
      }}
    >
      {children}
    </ExperimentIterationContext.Provider>
  );
}

export function useExperimentIterationContext() {
  const ctx = useContext(ExperimentIterationContext);

  if (!ctx) {
    throw new Error('useExperimentIterationContext must be used within an ExperimentIterationProvider');
  }

  return ctx;
}

// Split out so the flag is only ever fetched when the iteration actually has one. Experiments that
// assign from a warehouse source have no flag, and requesting an empty key would fetch a bogus URL.
function ExperimentIterationContextProviderWithFlag({
  featureFlagKey,
  experiment,
  iteration,
  children,
}: {
  featureFlagKey: string;
  experiment: Experiment;
  iteration: Iteration;
  children: React.ReactNode;
}) {
  const projectKey = useProjectKey();
  const { data: flag } = useSuspenseQuery(getFeatureFlagQuery({ projectKey, featureFlagKey }));

  return (
    <ExperimentIterationContextProvider value={{ experiment, iteration, flag }}>
      {children}
    </ExperimentIterationContextProvider>
  );
}

// Wraps the child in an ExperimentIterationContextProvider for the current or selected iteration
export function WithExperimentIterationContextProvider({ children }: { children: React.ReactNode }) {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const _experimentKey = useParam('experimentKey', { optional: true });
  const _multiArmedBanditKey = useParam('multiArmedBanditKey', { optional: true });
  const experimentKey = _experimentKey ?? _multiArmedBanditKey ?? '';

  // if no iterationId is found, default to the current iteration
  const iterationId = useParam('iterationId', { optional: true });

  const { data: experiment } = useSuspenseExperiment({
    projectKey,
    environmentKey,
    experimentKey,
    params: { expand: DEFAULT_EXPERIMENT_EXPAND_OPTIONS },
  });

  const iteration =
    !iterationId || iterationId === experiment?.currentIteration?._id
      ? experiment?.currentIteration
      : experiment?.previousIterations?.find((i) => i._id === iterationId);

  const featureFlagKey = Object.keys(iteration?.flags ?? {})?.[0];

  if (!iteration) {
    throw new Error('Iteration not found');
  }

  return (
    <Suspense fallback={<AppSkeletonBody bodyOnly />}>
      <QueryErrorBoundary
        fallbackRender={({ error }) =>
          error && error.status !== 404 && isRESTAPIError(error) ? (
            <div>
              <Message id="experiments.experimentIterationPage.loadError" values={{ message: error.message }} />
            </div>
          ) : null
        }
      >
        {featureFlagKey ? (
          <ExperimentIterationContextProviderWithFlag
            featureFlagKey={featureFlagKey}
            experiment={experiment}
            iteration={iteration}
          >
            {children}
          </ExperimentIterationContextProviderWithFlag>
        ) : (
          <ExperimentIterationContextProvider value={{ experiment, iteration }}>
            {children}
          </ExperimentIterationContextProvider>
        )}
      </QueryErrorBoundary>
    </Suspense>
  );
}
